import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, Form, Button, Spinner, Badge } from "react-bootstrap";
import { FaArrowLeft, FaArrowRight, FaMagic, FaCheck } from "react-icons/fa";

import { useDisciplines } from "../../../hooks/disciplines/useDisciplines";
import {
	questionGenerationAPI,
	type GenerationContext,
	type GeneratedQuestion,
} from "../../../hooks/questions/questionGenerationAPI";
import {
	mapEducationLevel,
	mapDifficultyLevel,
} from "../../../utils/questions/questionsMap";

const TIPOS = ["Questão objetiva", "Questão dissertativa"];
const ESCOLARIDADES = ["Ensino Fundamental", "Ensino Médio", "Ensino Superior"];
const DIFICULDADES = ["Fácil", "Médio", "Difícil"];

type Step = "context" | "summary" | "result";

const INITIAL: GenerationContext = {
	tema: "",
	contexto: "",
	tipoQuestao: "",
	nivelEscolaridade: "",
	nivelDificuldade: "",
	quantidadeAlternativas: 4,
	disciplina: "",
	disciplina_id: "",
};

// Fluxo de criação de questão por IA: contexto -> resumo do que será enviado
// -> geração -> aprovação (abre o cadastro pré-preenchido para editar/salvar).
export function QuestionAiCreateView() {
	const navigate = useNavigate();
	const token =
		useSelector((state: any) => state.auth.token) ||
		localStorage.getItem("token") ||
		"";

	const { discipline } = useDisciplines(token);

	const [step, setStep] = useState<Step>("context");
	const [form, setForm] = useState<GenerationContext>(INITIAL);
	const [error, setError] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [generated, setGenerated] = useState<GeneratedQuestion | null>(null);

	const isObjetiva = form.tipoQuestao === "Questão objetiva";

	const handleChange = (field: keyof GenerationContext, value: any) => {
		setForm((prev) => ({ ...prev, [field]: value }));
	};

	const handleDisciplineChange = (id: string) => {
		const found = discipline.find((d: any) => d.id === id);
		setForm((prev) => ({
			...prev,
			disciplina_id: id,
			disciplina: found?.name ?? "",
		}));
	};

	const contextIsValid =
		form.tema.trim() !== "" &&
		form.disciplina_id !== "" &&
		form.tipoQuestao !== "" &&
		form.nivelEscolaridade !== "" &&
		form.nivelDificuldade !== "" &&
		(!isObjetiva || Number(form.quantidadeAlternativas) >= 2);

	const goToSummary = () => {
		if (!contextIsValid) {
			setError("Preencha todos os campos obrigatórios.");
			return;
		}
		setError("");
		setStep("summary");
	};

	const handleGenerate = async () => {
		setLoading(true);
		setError("");
		try {
			const payload: GenerationContext = {
				...form,
				quantidadeAlternativas: isObjetiva
					? Number(form.quantidadeAlternativas)
					: undefined,
			};
			const result = await questionGenerationAPI.generate(payload, token);
			setGenerated(result);
			setStep("result");
		} catch (err: any) {
			setError(
				err?.response?.data?.error?.message ||
					"Não foi possível gerar a questão. Tente novamente."
			);
		} finally {
			setLoading(false);
		}
	};

	// Aprovar: abre a tela de cadastro já preenchida com a questão gerada.
	const handleApprove = () => {
		if (!generated) return;
		navigate("/questions/create", { state: { aiQuestion: generated } });
	};

	const summaryRows = useMemo(
		() => [
			["Disciplina", form.disciplina],
			["Tipo", form.tipoQuestao],
			["Escolaridade", form.nivelEscolaridade],
			["Dificuldade", form.nivelDificuldade],
			["Tema", form.tema],
			["Contexto", form.contexto || "—"],
			...(isObjetiva
				? [["Alternativas", String(form.quantidadeAlternativas)]]
				: []),
		],
		[form, isObjetiva]
	);

	return (
		<div className="row">
			<div className="col-12 py-4 px-3">
				<h3 className="mb-4">Criar questão com IA</h3>

				{error && <div className="alert alert-danger">{error}</div>}

				{step === "context" && (
					<Card className="shadow-sm border-0">
						<Card.Header>
							<strong>Defina o Contexto da Questão</strong>
							<div className="text-muted small">
								Crie uma questão utilizando inteligência artificial
							</div>
						</Card.Header>
						<Card.Body>
							<Form.Group className="mb-3">
								<Form.Label>
									Disciplina <span className="text-danger">*</span>
								</Form.Label>
								<Form.Select
									id="ai-disciplina"
									value={form.disciplina_id}
									onChange={(e) => handleDisciplineChange(e.target.value)}
								>
									<option value="">Selecione a disciplina</option>
									{discipline.map((d: any) => (
										<option key={d.id} value={d.id}>
											{d.name}
										</option>
									))}
								</Form.Select>
							</Form.Group>

							<Form.Group className="mb-3">
								<Form.Label>
									Tipo de questão <span className="text-danger">*</span>
								</Form.Label>
								<Form.Select
									id="ai-tipo"
									value={form.tipoQuestao}
									onChange={(e) => handleChange("tipoQuestao", e.target.value)}
								>
									<option value="">Selecione tipo de questão</option>
									{TIPOS.map((t) => (
										<option key={t} value={t}>
											{t}
										</option>
									))}
								</Form.Select>
							</Form.Group>

							<Form.Group className="mb-3">
								<Form.Label>
									Nível de Escolaridade <span className="text-danger">*</span>
								</Form.Label>
								<Form.Select
									id="ai-escolaridade"
									value={form.nivelEscolaridade}
									onChange={(e) =>
										handleChange("nivelEscolaridade", e.target.value)
									}
								>
									<option value="">Selecione nível de escolaridade</option>
									{ESCOLARIDADES.map((n) => (
										<option key={n} value={n}>
											{n}
										</option>
									))}
								</Form.Select>
							</Form.Group>

							<Form.Group className="mb-3">
								<Form.Label>
									Nível de Dificuldade <span className="text-danger">*</span>
								</Form.Label>
								<Form.Select
									id="ai-dificuldade"
									value={form.nivelDificuldade}
									onChange={(e) =>
										handleChange("nivelDificuldade", e.target.value)
									}
								>
									<option value="">Selecione nível de dificuldade</option>
									{DIFICULDADES.map((n) => (
										<option key={n} value={n}>
											{n}
										</option>
									))}
								</Form.Select>
							</Form.Group>

							{isObjetiva && (
								<Form.Group className="mb-3">
									<Form.Label>
										Quantidade de alternativas{" "}
										<span className="text-danger">*</span>
									</Form.Label>
									<Form.Select
										id="ai-qtd-alternativas"
										value={String(form.quantidadeAlternativas)}
										onChange={(e) =>
											handleChange(
												"quantidadeAlternativas",
												Number(e.target.value)
											)
										}
									>
										{[2, 3, 4, 5].map((n) => (
											<option key={n} value={n}>
												{n}
											</option>
										))}
									</Form.Select>
								</Form.Group>
							)}

							<Form.Group className="mb-3">
								<Form.Label>
									Tema principal <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									id="ai-tema"
									type="text"
									placeholder="Ex: Revolução Francesa"
									value={form.tema}
									onChange={(e) => handleChange("tema", e.target.value)}
								/>
							</Form.Group>

							<Form.Group className="mb-3">
								<Form.Label>Contexto</Form.Label>
								<Form.Control
									id="ai-contexto"
									as="textarea"
									rows={2}
									placeholder="Descreva o assunto específico da questão"
									value={form.contexto}
									onChange={(e) => handleChange("contexto", e.target.value)}
								/>
							</Form.Group>

							<div className="d-flex justify-content-between">
								<Button
									id="ai-btn-voltar"
									type="button"
									variant="secondary"
									onClick={() => navigate("/questions")}
								>
									<FaArrowLeft className="me-1" /> Voltar
								</Button>
								<Button
									id="ai-btn-avancar"
									type="button"
									variant="primary"
									onClick={goToSummary}
								>
									Avançar <FaArrowRight className="ms-1" />
								</Button>
							</div>
						</Card.Body>
					</Card>
				)}

				{step === "summary" && (
					<Card className="shadow-sm border-0">
						<Card.Header>
							<strong>Resumo do que será enviado para a IA</strong>
						</Card.Header>
						<Card.Body>
							<ul className="list-unstyled mb-4">
								{summaryRows.map(([label, value]) => (
									<li key={label} className="mb-2">
										<strong>{label}:</strong> {value}
									</li>
								))}
							</ul>
							<div className="d-flex justify-content-between">
								<Button
									id="ai-btn-voltar-resumo"
									type="button"
									variant="secondary"
									disabled={loading}
									onClick={() => setStep("context")}
								>
									<FaArrowLeft className="me-1" /> Voltar
								</Button>
								<Button
									id="ai-btn-gerar"
									type="button"
									variant="success"
									disabled={loading}
									onClick={handleGenerate}
								>
									{loading ? (
										<>
											<Spinner animation="border" size="sm" className="me-2" />
											Gerando...
										</>
									) : (
										<>
											<FaMagic className="me-1" /> Gerar questão
										</>
									)}
								</Button>
							</div>
						</Card.Body>
					</Card>
				)}

				{step === "result" && generated && (
					<Card className="shadow-sm border-0">
						<Card.Header className="d-flex justify-content-between align-items-center">
							<strong>Questão gerada — revise e aprove</strong>
							<Badge bg="info">
								{mapEducationLevel(generated.educationLevel)} ·{" "}
								{mapDifficultyLevel(generated.difficultyLevel)}
							</Badge>
						</Card.Header>
						<Card.Body>
							<div className="mb-3">
								<strong>Enunciado</strong>
								<p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
									{generated.statement}
								</p>
							</div>

							{generated.type === "objective" ? (
								<div className="mb-3">
									<strong>Alternativas</strong>
									<ul className="list-unstyled mt-2">
										{generated.objectives.map((o, idx) => (
											<li
												key={idx}
												className={`border rounded p-2 mb-2 ${
													o.isCorrect ? "border-success bg-light" : ""
												}`}
											>
												{o.isCorrect && (
													<FaCheck className="text-success me-2" />
												)}
												{String.fromCharCode(65 + idx)}) {o.text}
											</li>
										))}
									</ul>
								</div>
							) : (
								<div className="mb-3">
									<strong>Resposta esperada</strong>
									<p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
										{generated.answerJustification}
									</p>
								</div>
							)}

							{generated.keywords.length > 0 && (
								<div className="mb-3">
									<strong>Palavras-chave</strong>
									<div className="d-flex flex-wrap gap-1 mt-1">
										{generated.keywords.map((k) => (
											<Badge key={k} bg="secondary">
												{k}
											</Badge>
										))}
									</div>
								</div>
							)}

							<div className="d-flex justify-content-between">
								<Button
									id="ai-btn-regerar"
									type="button"
									variant="outline-secondary"
									disabled={loading}
									onClick={handleGenerate}
								>
									{loading ? "Gerando..." : "Gerar novamente"}
								</Button>
								<Button
									id="ai-btn-aprovar"
									type="button"
									variant="success"
									onClick={handleApprove}
								>
									<FaCheck className="me-1" /> Aprovar e revisar
								</Button>
							</div>
						</Card.Body>
					</Card>
				)}
			</div>
		</div>
	);
}

export default QuestionAiCreateView;
